<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import SocialIcon from '@/components/common/SocialIcon.vue'
import GroupSelector from '@/components/header/GroupSelector.vue'
import SidebarMenuItem from '@/components/layout/SidebarMenuItem.vue'
import SidebarPlayerCard from '@/components/layout/SidebarPlayerCard.vue'
import { useMenuAccordion } from '@/composables/useMenuAccordion'
import { useAuthStore } from '@/stores/auth'
import { useMenuStore } from '@/stores/menu'
import { useSocialStore } from '@/stores/social'
import { useUiStore } from '@/stores/ui'

/**
 * Collapsible sidebar holding the API-driven menu, preceded by the fixed
 * "Home" entry (served at `/`) which is not part of the backend tree.
 */
const props = withDefaults(
  defineProps<{
    /** Used by the mobile drawer, where narrowing to icons makes no sense. */
    forceExpanded?: boolean
  }>(),
  { forceExpanded: false },
)

const { t } = useI18n()
const auth = useAuthStore()
const ui = useUiStore()
const menu = useMenuStore()
const social = useSocialStore()
const route = useRoute()

const collapsed = computed(() => !props.forceExpanded && ui.sidebarCollapsed)
const isHome = computed(() => route.name === 'home')

/**
 * Fixed entries of the footer. They are not part of `GET /menu` on purpose:
 * help and contact must be reachable whatever the backend tree says, and for a
 * guest too.
 */
const FOOTER_LINKS = [
  { path: '/guides', icon: 'mdi-book-open-page-variant-outline', label: 'sidebar.manual' },
  { path: '/contact-us', icon: 'mdi-email-outline', label: 'sidebar.contactUs' },
]

// Top-level sections: one open at a time (each section does the same for its own).
const accordion = useMenuAccordion(
  () => menu.visibleItems,
  () => route.path,
)

// Decorative and public: no token needed, and a failure just leaves the row empty.
onMounted(() => void social.fetch())
</script>

<template>
  <aside
    class="chrome-surface flex shrink-0 flex-col border-r border-black/30 transition-[width] duration-200"
    :class="collapsed ? 'w-16' : 'w-64'"
    :aria-label="t('sidebar.navigation')"
  >
    <!--
      Drawer only. Two things live here:
      · the close button — the header hamburger that opened the drawer sits
        under its overlay, so it cannot be the way back out;
      · below md the group selector, because up there the header only has room
        for the server one. From md up the header shows it again, and the
        docked desktop sidebar (lg and up) never renders it at all.
    -->
    <div v-if="props.forceExpanded" class="flex items-center gap-2 border-b border-white/10 p-2">
      <div class="min-w-0 flex-1 md:hidden">
        <GroupSelector />
      </div>
      <button
        type="button"
        class="chrome-item ml-auto cursor-pointer px-2"
        :aria-label="t('common.close')"
        :title="t('common.close')"
        @click="ui.closeMobileSidebar()"
      >
        <AppIcon name="mdi-close" class="text-lg" />
      </button>
    </div>

    <!--
      The character being played, outside the scrolling <nav> so it stays in
      view however long the menu is. Only once logged in: a guest has no
      character, and the card draws nothing when there is none to show.
    -->
    <SidebarPlayerCard v-if="auth.isAuthenticated" :collapsed="collapsed" />

    <nav class="flex-1 overflow-y-auto p-2">
      <ul class="space-y-0.5">
        <!--
          Fixed entry: the home is always reachable — shown only once logged in,
          because for a guest `/` is the landing page, not this home.
        -->
        <li v-if="auth.isAuthenticated">
          <RouterLink :to="{ name: 'home' }" custom>
            <template #default="{ navigate, href }">
              <a
                :href="href"
                class="chrome-item"
                :class="{ 'chrome-item-active': isHome }"
                :aria-current="isHome ? 'page' : undefined"
                :title="collapsed ? t('common.home') : undefined"
                @click="navigate"
              >
                <AppIcon name="mdi-home" class="text-gold-400 text-lg" />
                <span v-if="!collapsed" class="truncate">{{ t('common.home') }}</span>
              </a>
            </template>
          </RouterLink>
        </li>

        <li v-if="auth.isAuthenticated && menu.visibleItems.length > 0" aria-hidden="true">
          <hr class="my-2 border-white/10" />
        </li>

        <SidebarMenuItem
          v-for="item in menu.visibleItems"
          :key="item.id"
          :item="item"
          :collapsed="collapsed"
          :open="accordion.isOpen(item.id)"
          @toggle="accordion.toggle(item.id)"
        />
      </ul>
    </nav>

    <!--
      Fixed foot: it sits outside the scrolling <nav> above, so help, contact
      and the social icons stay in view however long the menu grows. The
      collapse control used to live here and is now the header hamburger, which
      is reachable at every width.

      Expanded it is two rows — help and contact side by side, then social
      icons and the privacy link — so on a phone it doesn't eat a third of the
      drawer. The collapsed rail is 4rem wide and holds none of that side by
      side, so there everything stacks in a column of icons.
    -->
    <div class="shrink-0 border-t border-white/10 p-2">
      <ul :class="collapsed ? 'space-y-0.5' : 'grid grid-cols-2 gap-1'">
        <li v-for="link in FOOTER_LINKS" :key="link.path" class="min-w-0">
          <RouterLink :to="link.path" custom>
            <template #default="{ navigate, href, isActive }">
              <a
                :href="href"
                class="chrome-item"
                :class="[
                  { 'chrome-item-active': isActive },
                  collapsed ? '' : 'min-h-10 justify-center gap-1.5 px-2 text-xs',
                ]"
                :aria-current="isActive ? 'page' : undefined"
                :title="collapsed ? t(link.label) : undefined"
                @click="navigate"
              >
                <AppIcon :name="link.icon" :class="collapsed ? 'text-lg' : 'text-base'" />
                <span v-if="!collapsed" class="truncate">{{ t(link.label) }}</span>
              </a>
            </template>
          </RouterLink>
        </li>
      </ul>

      <div
        class="mt-1 flex items-center border-t border-white/10 pt-1"
        :class="collapsed ? 'flex-col gap-1' : 'justify-between gap-2'"
      >
        <!-- Only the networks an admin actually configured are drawn. -->
        <nav
          v-if="social.entries.length > 0"
          class="flex items-center gap-0.5"
          :class="{ 'flex-col': collapsed }"
          :aria-label="t('sidebar.followUs')"
        >
          <a
            v-for="entry in social.entries"
            :key="entry.key"
            :href="entry.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-parchment-200/70 hover:text-gold-400 flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-white/10"
            :title="t(entry.label)"
            :aria-label="t(entry.label)"
          >
            <SocialIcon :name="entry.key" />
          </a>
        </nav>

        <!--
          Fine print, not a menu entry: the policy has to be reachable from every
          page, but it is not somewhere people navigate to. On the collapsed rail
          the words don't fit, so it shrinks to the shield icon.
        -->
        <RouterLink
          :to="{ name: 'privacy-policy' }"
          class="text-parchment-200/60 hover:text-parchment-100 flex min-h-10 min-w-0 items-center justify-center gap-1 text-xs hover:underline"
          :class="{ 'mx-auto': !collapsed && social.entries.length === 0 }"
          :title="collapsed ? t('sidebar.privacy') : undefined"
          :aria-label="collapsed ? t('sidebar.privacy') : undefined"
        >
          <AppIcon v-if="collapsed" name="mdi-shield-lock-outline" class="text-base" />
          <span v-else class="truncate">{{ t('sidebar.privacy') }}</span>
        </RouterLink>
      </div>
    </div>
  </aside>
</template>
